import { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import styled from "styled-components";
import { Button } from "../Button";
import { theme } from "../../styles/theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useDecisionAccess, { STATUS_LABELS } from "../../hooks/useDecisionAccess";
import { changeDecisionStatus, getDecisionStatuses } from "../../api/decision/access";

const StyledStatusControl = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.8rem;
	margin: 0.8rem 0 1.2rem;
	font-family: ${theme.font.secondary};

	.current {
		color: ${theme.color.gray};
	}

	.confirm {
		flex-basis: 100%;
		color: ${theme.color.gray0};
	}
`;

const ACTIONS = {
	ongoing: { label: "Move to ongoing" },
	draft: { label: "Back to draft" },
	done: {
		label: "Mark as done",
		confirm: "Done decisions are read-only and become visible to the readers. Continue?",
	},
	archive: { label: "Archive", confirm: "Archived decisions stay read-only. Continue?" },
};

/** Status of the decision, status changes the current user may make. */
export default function DecisionStatusControl({ id, status }) {
	const { axiosPrivate } = useAxiosPrivate();
	const { permissions, refreshDecision } = useDecisionAccess(id);
	const { data: statuses = [] } = useQuery("decision-statuses", () => getDecisionStatuses(axiosPrivate), {
		enabled: Boolean(permissions.changeStatus),
		staleTime: Infinity,
	});
	const [pending, setPending] = useState(null);
	const [busy, setBusy] = useState(false);

	const next = permissions.changeStatus ? permissions.nextStatuses ?? [] : [];

	const change = async (slug) => {
		const target = statuses.find((s) => s.slug === slug);
		if (!target) return;
		setBusy(true);
		try {
			await changeDecisionStatus(axiosPrivate, id, target.id);
			refreshDecision();
		} catch (e) {
			// The request helper already showed the error
		} finally {
			setBusy(false);
			setPending(null);
		}
	};

	return (
		<StyledStatusControl>
			<span className="current">{STATUS_LABELS[status] ?? status}</span>
			{next.map((slug) => (
				<Button
					key={slug}
					small
					bg={slug === "done"}
					disabled={busy || !statuses.length}
					onClick={() => (ACTIONS[slug]?.confirm ? setPending(slug) : change(slug))}
				>
					{ACTIONS[slug]?.label ?? STATUS_LABELS[slug]}
				</Button>
			))}
			{pending && (
				<>
					<span className="confirm">{ACTIONS[pending].confirm}</span>
					<Button small disabled={busy} onClick={() => change(pending)}>
						{ACTIONS[pending].label}
					</Button>
					<Button small bg={false} onClick={() => setPending(null)}>
						Cancel
					</Button>
				</>
			)}
		</StyledStatusControl>
	);
}

DecisionStatusControl.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	status: PropTypes.string,
};
